import { createContext, useContext, useEffect, useState } from "react";
import { useUserSettings } from "../context/userSettingsContext";
import de from "../i18n/de";
import en from "../i18n/en";
import es from "../i18n/es";
import fr from "../i18n/fr";
import it from "../i18n/it";
import { LanguageCode } from "../types";

export type Translation = typeof en;
export type TranslationKeys = keyof Translation;

const translations: Record<string, Translation> = {
  en,
  it,
  de,
  fr,
  es,
};

export type I18n = {
  t: Translation;
  language: LanguageCode;
  setLanguage: (lang: LanguageCode) => void;
  errorsMap: Map<number, string>;
};

const I18nContext = createContext<I18n | null>(null);

export const I18nProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const userSettings = useUserSettings();
  const [language, setLanguageState] = useState<LanguageCode>(userSettings.me.preferredLanguage ?? "en-gb");

  useEffect(() => {
    if (userSettings.me.preferredLanguage) {
      setLanguageState(userSettings.me.preferredLanguage);
    }
  }, [userSettings.me.preferredLanguage]);

  const t = translations[language.substring(0, 2)] ?? en;

  const errorsMap = new Map<number, string>([
    [100008, t.users.errors.rothoblaasUserWithEmailNotFound],
    [100009, t.users.errors.usersWithEmailAlreadyExists],
    [100010, t.users.errors.inUseByCarTasks],
    [100011, t.users.errors.inUseByCarOrders],
    [100012, t.users.errors.inUseByCarDrivers],
    [100021, t.users.errors.invalidEmailDomain],
    [100022, t.users.errors.userSubsidiaryAlreadyExists],
    [100027, t.users.errors.userInUseAsManager],

    [200001, t.common.resourceNotFound],
    [200012, t.cars.errors.vinAlreadyUsed],
    [200013, t.cars.errors.carReadNotAllowed],
    [200019, t.common.alreadyUpdated],

    [300004, t.subsidiaries.errors.nameAlreadyExists],
    [300006, t.subsidiaries.errors.inUseByCarOrders],
    [300007, t.subsidiaries.errors.inUseByCars],
    [300012, t.subsidiaries.errors.missingCountryIsoCode],
    [300013, t.subsidiaries.errors.countryISOCodeAlreadyExists],
    [300014, t.common.alreadyUpdated],
    [300015, t.subsidiaries.errors.missingVatServiceCode],
    [300016, t.subsidiaries.errors.missingVatDeductionCode],
    [300017, t.subsidiaries.errors.missingSemiramisOrganizationCode],

    [400002, t.leasingCompanies.errors.nameAlreadyExists],
    [400003, t.leasingCompanies.errors.inUseByCarLeasings],
    [400008, t.common.alreadyUpdated],

    [500003, t.manufacturers.errors.inUseByCarOrders],
    [500004, t.manufacturers.errors.inUseByCars],
    [500005, t.manufacturers.errors.nameAlreadyUsed],
    [500010, t.common.alreadyUpdated],

    [600003, t.suppliers.errors.inUseByCarOrders],
    [600004, t.suppliers.errors.nameAlreadyUsed],
    [600010, t.common.alreadyUpdated],

    [700009, t.common.alreadyUpdated],
    [700002, t.carLeasings.errors.alreadyHasLeasing],

    [800017, t.common.alreadyUpdated],
    [800019, t.carOrders.errors.carOrderDateAfterSoldDate],

    [900012, t.carPlates.errors.plateOverlapPeriodSub],
    [900013, t.carPlates.errors.plateOverlapPeriodSub],
    [900019, t.carPlates.errors.plateOverlapPeriodCar],
    [900020, t.carPlates.errors.plateOverlapPeriodSub],
    [900021, t.common.alreadyUpdated],
    [900022, t.carPlates.errors.carPlateOutOfSubsidiaryPeriod],
    [900023, t.carPlates.errors.carPlateOutOfSubsidiaryPeriod],

    [1000014, t.carTasks.errors.inUseByWorkflows],

    [1100018, t.carDrivers.errors.overlapsPeriod],
    [1100021, t.carDrivers.errors.overlapsPeriodPool],
    [1100025, t.carDrivers.errors.carDriverOutOfSubsidiaryPeriod],
    [1100026, t.carDrivers.errors.carDriverOutOfSubsidiaryPeriod],
    [1100028, t.carDrivers.errors.delegationLanguageNotFound],
    [1100029, t.carDrivers.errors.delegationSubsidiaryNotConfigured],

    [1200013, t.highwayPasses.errors.highwayPassOverlaps],
    [1200019, t.highwayPasses.errors.highwayPassOverlaps],
    [1200020, t.common.alreadyUpdated],
    [1200021, t.highwayPasses.errors.highwayPassOutOfSubsidiaryPeriod],
    [1200022, t.highwayPasses.errors.highwayPassOutOfSubsidiaryPeriod],

    [1300003, t.common.invalidDate],
    [1300008, t.mileages.errors.distanceLowerPrevRecord],
    [1300009, t.mileages.errors.distanceHigherNextRecord],
    [1300019, t.mileages.errors.mileageOutOfCarDriverPeriod],
    [1300020, t.common.alreadyUpdated],
    [1300024, t.mileages.errors.mileageWithThisDateAlreadyExists],
    [1300025, t.mileages.errors.mileageOutOfSubsidiaryPeriod],
    [1300026, t.mileages.errors.reminderMissingCurrentDriver],
    [1300027, t.mileages.errors.reminderDriverMissingEmail],
    [1300028, t.mileages.errors.reminderDriverNotificationsDisabled],
    [1300029, t.mileages.errors.reminderSubsidiaryNotAllowed],

    [1400003, t.tasks.errors.taskNameAlreadyExists],
    [1400008, t.common.alreadyUpdated],
    [1400009, t.tasks.errors.inUseByCustomTasks],

    [1500003, t.costTypeCategories.errors.nameAlreadyUsed],
    [1500004, t.costTypeCategories.errors.isUsedByCostType],
    [1500009, t.common.alreadyUpdated],

    [1600003, t.costTypes.errors.nameAlreadyUsed],
    [1600009, t.costTypes.errors.isUsedByCosts],
    [1600015, t.common.alreadyUpdated],

    [1700013, t.common.alreadyUpdated],
    [1700015, t.costs.errors.costOutOfSubsidiaryPeriod],
    [1700016, t.costs.errors.costOutOfSubsidiaryPeriod],

    [1800002, t.dueDateTypes.errors.nameAlreadyUsed],
    [1800004, t.dueDateTypes.errors.isUsedByDueDates],
    [1800009, t.common.alreadyUpdated],

    [1900012, t.common.alreadyUpdated],
    [1900013, t.dueDates.errors.dueDateOutOfSubsidiaryPeriod],
    [1900014, t.dueDates.errors.dueDateOutOfSubsidiaryPeriod],

    [2700003, t.documents.errors.notFound],

    [2900012, t.carPoolCostCenters.errors.carPoolOverlapPeriod],

    [3000012, t.otherCarAssignments.errors.carPoolOverlapPeriod],
    [3000013, t.otherCarAssignments.errors.carPoolOverlapPeriodWithDrivers],
    [3000019, t.common.alreadyUpdated],
    [3000021, t.otherCarAssignments.errors.assignmentOutOfSubsidiaryPeriod],
    [3000022, t.otherCarAssignments.errors.assignmentOutOfSubsidiaryPeriod],

    [3200003, t.documents.errors.notFound],

    [3300019, t.carSubsidiaries.errors.overlapPeriodCarPlate],
    [3300020, t.carSubsidiaries.errors.overlapPeriodCarDriver],

    [3500001, t.documents.errors.notFound],

    [3600012, t.carPlannings.errors.missingSemiramisCode],

    [3700009, t.documents.errors.notFound],

    [3800010, t.documents.errors.notFound],

    [4000003, t.rentalCars.errors.overlapPeriodCar],

    [4400003, t.maintenanceTypes.errors.nameAlreadyUsed],
    [4400004, t.maintenanceTypes.errors.isUsedByMaintenance],

    [4700002, t.claimTypes.errors.nameAlreadyUsed],
    [4700004, t.claimTypes.errors.inUseByClaims],

    [4900014, t.common.alreadyUpdated],
    [4900015, t.fines.errors.notifyArchived],
    [4900017, t.fines.errors.alreadyArchived],
    [4900018, t.fines.errors.driverMissingEmail],
    [4900019, t.fines.errors.driverNotificationsDisabled],
    [4900020, t.fines.notifyDialog.noAttachments],

    [4100002, t.costsImport.dkv.errors.columnTargaVeicolo],
    [4100003, t.costsImport.dkv.errors.columnDataFattura],
    [4100004, t.costsImport.dkv.errors.columnOraTransazione],
    [4100005, t.costsImport.dkv.errors.columnValoreBaseNetto],
    [4100006, t.costsImport.dkv.errors.columnValoreBaseLordo],
    [4100007, t.costsImport.dkv.errors.columnCodiceMerce],
    [4100008, t.costsImport.dkv.errors.columnGruppoProdotti],
    [4100009, t.costsImport.dkv.errors.columnTipoMerce],
    [4100010, t.costsImport.dkv.errors.columnPaesePedaggio],
    [4100011, t.costsImport.dkv.errors.columnIva],
    [4100012, t.costsImport.dkv.errors.columnScontoNetto],
    [4100013, t.costsImport.dkv.errors.columnScontoLordo],
    [4100014, t.costsImport.dkv.errors.columnServiceFeeNetto],
    [4100015, t.costsImport.dkv.errors.columnQuantita],
    [4100016, t.costsImport.dkv.errors.columnUnita],

    [4200002, t.costsImport.dkvTolls.errors.columnDataFattura],
    [4200003, t.costsImport.dkvTolls.errors.columnTargaVeicolo],
    [4200004, t.costsImport.dkvTolls.errors.columnOraUscita],
    [4200005, t.costsImport.dkvTolls.errors.columnCodiceMerce],
    [4200006, t.costsImport.dkvTolls.errors.columnValutaServizio],
    [4200007, t.costsImport.dkvTolls.errors.columnIva],
    [4200008, t.costsImport.dkvTolls.errors.columnValoreBaseNetto],
    [4200009, t.costsImport.dkvTolls.errors.columnPaesePedaggio],

    [5000003, t.documents.errors.notFound],
  ]);

  return (
    <I18nContext.Provider value={{ t, language, setLanguage: setLanguageState, errorsMap }}>
      {children}
    </I18nContext.Provider>
  );
};

export const useI18n = () => {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error("useI18n must be used inside an I18nProvider");
  }
  return context;
};
